import { useEffect, useMemo, useState } from "react";

import { useOnboardingStore } from "../store/useOnboardingStore";

const TOOLTIP_WIDTH = 340;

const TOOLTIP_HEIGHT = 230;

const SCREEN_PADDING = 24;
import { api, formatApiError } from "../lib/api";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";

export default function OnboardingOverlay({ steps = [] }) {
  const { refresh } = useAuth();
  const {
    isOpen,
    currentStepIndex,
    currentTour,
    nextStep,
    prevStep,
    closeTour,
    finishTour,
  } = useOnboardingStore();

  const step = useMemo(() => {
    return steps[currentStepIndex];
  }, [steps, currentStepIndex]);

  const [targetRect, setTargetRect] = useState(null);

  // =====================================================
  // FIND TARGET ELEMENT
  // =====================================================

  useEffect(() => {
    if (!isOpen || !step) return;

    let cancelled = false;
    let attempts = 0;
    let retryTimer;
    let measureTimer;
    let cleanupListeners = () => {};

    const measure = (el) => {
      const rect = el.getBoundingClientRect();
      setTargetRect({
        top: rect.top,
        left: rect.left,
        width: rect.width,
        height: rect.height,
        bottom: rect.bottom,
        right: rect.right,
      });
    };

    const locate = () => {
      if (cancelled) return;

      const el = step.target ? document.querySelector(step.target) : null;

      if (el) {
        el.scrollIntoView({ behavior: "smooth", block: "center", inline: "center" });
        measureTimer = setTimeout(() => {
          if (!cancelled) measure(el);
        }, 300);

        const onMove = () => measure(el);
        window.addEventListener("resize", onMove, true);
        window.addEventListener("scroll", onMove, true);
        cleanupListeners = () => {
          window.removeEventListener("resize", onMove, true);
          window.removeEventListener("scroll", onMove, true);
        };
        return;
      }

      // The anchor may not be mounted yet — retry briefly, then skip the step
      // so a single missing target can never leave the tour stuck/blank.
      attempts += 1;
      if (attempts > 25) {
        console.warn("Tour target not found, skipping:", step.target);
        if (currentStepIndex < steps.length - 1) nextStep();
        else closeTour();
        return;
      }
      retryTimer = setTimeout(locate, 120);
    };

    locate();

    return () => {
      cancelled = true;
      clearTimeout(retryTimer);
      clearTimeout(measureTimer);
      cleanupListeners();
    };
  }, [isOpen, step, currentStepIndex, steps.length, nextStep, closeTour]);

  // =====================================================
  // EXIT
  // =====================================================

  if (!isOpen || !step || !targetRect) {
    return null;
  }

  // =====================================================
  // TOOLTIP POSITIONING
  // =====================================================

  let tooltipTop = targetRect.bottom + 18;

  let tooltipLeft = targetRect.left;

  // ---------------------------------------------------
  // RIGHT OVERFLOW
  // ---------------------------------------------------

  if (tooltipLeft + TOOLTIP_WIDTH > window.innerWidth - SCREEN_PADDING) {
    tooltipLeft = window.innerWidth - TOOLTIP_WIDTH - SCREEN_PADDING;
  }

  // ---------------------------------------------------
  // LEFT OVERFLOW
  // ---------------------------------------------------

  if (tooltipLeft < SCREEN_PADDING) {
    tooltipLeft = SCREEN_PADDING;
  }

  // ---------------------------------------------------
  // BOTTOM OVERFLOW
  // ---------------------------------------------------

  if (tooltipTop + TOOLTIP_HEIGHT > window.innerHeight - SCREEN_PADDING) {
    tooltipTop = targetRect.top - TOOLTIP_HEIGHT - 18;
  }

  // ---------------------------------------------------
  // TOP OVERFLOW
  // ---------------------------------------------------

  if (tooltipTop < SCREEN_PADDING) {
    tooltipTop = SCREEN_PADDING;
  }

  const tooltipStyle = {
    top: tooltipTop,
    left: tooltipLeft,
  };

  // =====================================================
  // HANDLE NEXT
  // =====================================================

  // Persist completion to the backend so it also sticks across devices. The
  // local persisted store already prevents re-showing, so this is best-effort.
  const persistComplete = async () => {
    try {
      await api.patch("/auth/onboarding", {
        has_completed_onboarding: true,
      });
      await refresh();
    } catch (err) {
      // Non-fatal: the local store has already dismissed the tour.
    }
  };

  // Skipping permanently dismisses the tour (local + backend).
  const handleSkip = async () => {
    closeTour();
    persistComplete();
  };

  const handleNext = async () => {
    // custom actions

    if (step?.action === "start-guided-setup") {
      closeTour();
      persistComplete();

      window.location.href = "/dashboard/assets";

      return;
    }

    // FINAL STEP

    if (currentStepIndex === steps.length - 1) {
      await persistComplete();

      finishTour(currentTour);

      return;
    }

    nextStep();
  };

  // =====================================================
  // HIGHLIGHT STYLE
  // =====================================================

  const highlightStyle = {
    top: targetRect.top - 8,
    left: targetRect.left - 8,
    width: targetRect.width + 16,
    height: targetRect.height + 16,
  };

  // =====================================================
  // RENDER
  // =====================================================

  return (
    <>
      {/* ================================================= */}
      {/* DARK BACKDROP */}
      {/* ================================================= */}

      <div className="fixed inset-0 z-[9998] bg-black/72 backdrop-blur-[1px]" />

      {/* ================================================= */}
      {/* HIGHLIGHT */}
      {/* ================================================= */}

      <div
        className="fixed z-[9999] rounded-3xl ring-4 ring-emerald-400 shadow-[0_0_0_9999px_rgba(0,0,0,0.72)] transition-all duration-300 pointer-events-none"
        style={highlightStyle}
      />

      {/* ================================================= */}
      {/* TOOLTIP */}
      {/* ================================================= */}

      <div
        className="fixed z-[10000] w-[340px] rounded-3xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 shadow-2xl p-6"
        style={tooltipStyle}
      >
        {/* STEP */}

        <div className="text-[11px] uppercase tracking-[0.25em] text-emerald-600 font-semibold">
          Step {currentStepIndex + 1} / {steps.length}
        </div>

        {/* TITLE */}

        <h3 className="mt-3 text-xl font-bold text-slate-900 dark:text-white">
          {step.title}
        </h3>

        {/* DESCRIPTION */}

        <p className="mt-3 text-sm leading-7 text-slate-600 dark:text-slate-300">
          {step.description}
        </p>

        {/* ACTIONS */}

        <div className="mt-6 flex items-center justify-between">
          <button
            onClick={handleSkip}
            className="text-sm text-slate-500 hover:text-slate-700 dark:hover:text-slate-300 transition-colors"
          >
            Skip Tour
          </button>

          <div className="flex items-center gap-2">
            {currentStepIndex > 0 && (
              <button
                onClick={prevStep}
                className="px-4 py-2 rounded-xl border border-slate-300 dark:border-slate-700 text-sm font-medium text-slate-700 dark:text-slate-200"
              >
                Back
              </button>
            )}

            <button
              onClick={handleNext}
              className="px-5 py-2 rounded-xl bg-emerald-600 hover:bg-emerald-700 transition-colors text-white text-sm font-semibold"
            >
              {currentStepIndex === steps.length - 1 ? "Finish" : "Next"}
            </button>
          </div>
        </div>
      </div>
    </>
  );
}
